import { storeToRefs } from 'pinia';
import { createRouter, createWebHistory, createMemoryHistory } from 'vue-router';

import authenticate from '@js/credentials/authenticate';

import { endpoint } from '@js/dom/parent';
import googleAnalytics from '@js/dom/analytics';

import useAuthStore from '@/stores/auth';

import blank from './blank';

let _app;

export default {
  get app() {
    if (_app) {
      return _app;
    }

    _app = createRouter({
      history: endpoint() ? createMemoryHistory() : createWebHistory(),
      routes: [{
        path: '/',
        name: 'home-index',
        meta: { public: true, title: '404', subtitle: 'Por favor, escolha um endpoint existente' },
        component: () => import('@/views/Home/Message.vue'),
      }, {
        path: '/blank',
        name: 'home-blank',
        meta: { public: true, title: '204', subtitle: 'Nenhum conteúdo a ser exibido' },
        component: () => import('@/views/Home/Message.vue'),
      }, {
        path: '/unauthorized',
        name: 'home-unauthorized',
        meta: { public: true, title: '401', subtitle: 'O token (authorization) informado é inválido' },
        component: () => import('@/views/Home/Message.vue'),
      }, {
        path: '/:matches(.*)*',
        name: 'home-404',
        meta: { public: true, title: '404', subtitle: 'Por favor, escolha um endpoint existente' },
        component: () => import('@/views/Home/Message.vue'),
      }, {
        path: '/preview',
        name: 'preview-index',
        meta: { public: true },
        component: () => import('@/views/Preview/Index.vue'),
        children: [{
          path: 'faq',
          name: 'preview-faq',
          component: () => import('@/views/Preview/FAQ.vue'),
        }, {
          path: ':embed',
          name: 'preview-embed',
          component: () => import('@/views/Preview/Blank.vue'),
        }],
      }, {
        path: '/customers/form',
        name: 'customers-form',
        component: () => import('@/views/Customers/Form.vue'),
      }, {
        path: '/customers/read',
        name: 'customers-read',
        component: () => import('@/views/Customers/Read.vue'),
      }, {
        path: '/customers/list',
        name: 'customers-list',
        component: () => import('@/views/Customers/List.vue'),
      }, {
        path: '/customers/schema',
        name: 'customers-schema',
        meta: { public: true },
        component: () => import('@/views/Customers/Schema.vue'),
      }, {
        path: '/proposals/form',
        name: 'proposals-form',
        component: () => import('@/views/Proposals/Form.vue'),
      }, {
        path: '/proposals/success',
        name: 'proposals-success',
        component: () => import('@/views/Proposals/Success.vue'),
      }, {
        path: '/proposals/list',
        name: 'proposals-list',
        component: () => import('@/views/Proposals/List.vue'),
      }, {
        // The path is the host's (contract); the approved are a screen of their own, not a filter.
        path: '/proposals/approved',
        name: 'proposals-approved',
        component: () => import('@/views/Proposals/Approved.vue'),
      }, {
        path: '/simulations/read',
        name: 'simulations-read',
        component: () => import('@/views/Simulations/Read.vue'),
      }, {
        path: '/simulations/form',
        name: 'simulations-form',
        component: () => import('@/views/Simulations/Form.vue'),
      }, {
        // The path here is the host's (contract); the API endpoint is not the same.
        path: '/simulations/list',
        name: 'simulations-list',
        component: () => import('@/views/Simulations/List.vue'),
      }, {
        path: '/vehicles/form',
        name: 'vehicles-form',
        component: () => import('@/views/Vehicles/Form.vue'),
      }, {
        path: '/vehicles/import',
        name: 'vehicles-import',
        component: () => import('@/views/Vehicles/Import.vue'),
      }, {
        path: '/vehicles/showroom',
        name: 'vehicles-showroom',
        component: () => import('@/views/Vehicles/Showroom.vue'),
      }, {
        path: '/vehicles/find',
        name: 'vehicles-find',
        component: () => import('@/views/Vehicles/Find.vue'),
      }, {
        path: '/vehicles/list',
        name: 'vehicles-list',
        component: () => import('@/views/Vehicles/List.vue'),
      }, {
        path: '/vehicle_models/form',
        name: 'vehicle-models-form',
        component: () => import('@/views/VehicleModels/Form.vue'),
      }, {
        path: '/vehicle_models/list',
        name: 'vehicle-models-list',
        component: () => import('@/views/VehicleModels/List.vue'),
      }, {
        // The path is the host's (contract) and the `name` is ours.
        path: '/user_invitations/list',
        name: 'user-invitations-list',
        component: () => import('@/views/Invitations/List.vue'),
      }, {
        // The sibling tab of the listing above, and a page of the host like it.
        path: '/user_invitations/form',
        name: 'user-invitations-form',
        component: () => import('@/views/Invitations/Form.vue'),
      }, {
        path: '/inbox/conversations',
        name: 'inbox-conversations',
        component: () => import('@/views/Inbox/List.vue'),
      }, {
        // The path is the endpoint the host mounts (contract); the `name` is ours alone.
        path: '/funnel/contacts',
        name: 'funnel-contacts',
        component: () => import('@/views/Funnel/Board.vue'),
      }, {
        path: '/denied_rescues/list',
        name: 'denied-rescues-list',
        component: () => import('@/views/DeniedRescues/List.vue'),
      }, {
        // Every public simulator screen lives under /public_simulator/. The path is the
        // contract the host builds `CREDERE.src` from; the `name` is ours alone.
        path: '/public_simulator/settings/form',
        name: 'public-simulator-settings-form',
        component: () => import('@/views/PublicSimulator/Settings/Form.vue'),
      }, {
        path: '/public_simulator/connections/form',
        name: 'public-simulator-connections-form',
        component: () => import('@/views/PublicSimulator/Connections/Form.vue'),
      }, {
        path: '/public_simulator/stock_sync/form',
        name: 'public-simulator-stock-sync-form',
        component: () => import('@/views/PublicSimulator/StockSync/Form.vue'),
      }, {
        path: '/public_simulator/automations/form',
        name: 'public-simulator-automations-form',
        component: () => import('@/views/PublicSimulator/Automations/Form.vue'),
      }, {
        // The row emits `public_simulator_automation:read` and the host opens the form.
        path: '/public_simulator/automations/list',
        name: 'public-simulator-automations-list',
        component: () => import('@/views/PublicSimulator/Automations/List.vue'),
      }, {
        path: '/public_simulator/notifications/form',
        name: 'public-simulator-notifications-form',
        component: () => import('@/views/PublicSimulator/Notifications/Form.vue'),
      }, {
        path: '/public_simulator/notifications/list',
        name: 'public-simulator-notifications-list',
        component: () => import('@/views/PublicSimulator/Notifications/List.vue'),
      }, {
        // Edits the same entity as the settings screen above, so saving it emits
        // `public_simulator_setting:update` and not a type of its own.
        path: '/public_simulator/answers/form',
        name: 'public-simulator-answers-form',
        component: () => import('@/views/PublicSimulator/Answers/Form.vue'),
      }, {
        // `new` and `edit` are both this route; what tells them apart is the id the host injects.
        path: '/public_simulator/questions/form',
        name: 'public-simulator-questions-form',
        component: () => import('@/views/PublicSimulator/Questions/Form.vue'),
      }, {
        path: '/public_simulator/questions/list',
        name: 'public-simulator-questions-list',
        component: () => import('@/views/PublicSimulator/Questions/List.vue'),
      }, {
        path: '/public_simulator/find',
        name: 'public-simulator-find',
        meta: { public: true },
        component: () => import('@/views/PublicSimulator/Find/Form.vue'),
      }, {
        path: '/public_simulator/contact',
        name: 'public-simulator-contact',
        meta: { public: true },
        component: () => import('@/views/PublicSimulator/Contact/Form.vue'),
      }, {
        path: '/public_simulator/success',
        name: 'public-simulator-success',
        meta: { public: true },
        component: () => import('@/views/PublicSimulator/Success.vue'),
      }, {
        path: '/onboarding/register',
        name: 'onboarding-register',
        meta: { public: true },
        component: () => import('@/views/Onboarding/Register.vue'),
      }, {
        path: '/onboarding/profile',
        name: 'onboarding-profile',
        meta: { public: true },
        component: () => import('@/views/Onboarding/Profile.vue'),
      }, {
        path: '/onboarding/store',
        name: 'onboarding-store',
        meta: { public: true },
        component: () => import('@/views/Onboarding/Store.vue'),
      }, {
        path: '/onboarding/address',
        name: 'onboarding-address',
        meta: { public: true },
        component: () => import('@/views/Onboarding/Address.vue'),
      }, {
        path: '/onboarding/subscription',
        name: 'onboarding-subscription',
        meta: { public: true },
        component: () => import('@/views/Onboarding/Subscription.vue'),
      }, {
        path: '/onboarding/credentials',
        name: 'onboarding-credentials',
        meta: { public: true },
        component: () => import('@/views/Onboarding/Credentials.vue'),
      }, {
        path: '/reports/score',
        name: 'reports-score',
        component: () => import('@/views/Reports/Score.vue'),
      }, {
        path: '/system_alerts/form',
        name: 'system-alerts-form',
        component: () => import('@/views/SystemAlerts/Form.vue'),
      }, {
        path: '/system_alerts/list',
        name: 'system-alerts-list',
        component: () => import('@/views/SystemAlerts/List.vue'),
      }, {
        path: '/bank_commission_rules/form',
        name: 'bank-commission-rules-form',
        component: () => import('@/views/BankCommissionRules/Form.vue'),
      }, {
        path: '/bank_ranking/form',
        name: 'bank-ranking-form',
        component: () => import('@/views/BankRanking/Form.vue'),
      }, {
        // The same screen twice: the route is the only difference, and its `purpose` also
        // names the group the host writes settings under (`CREDERE.settings.<purpose>.stores`).
        path: '/commercial_control/stores',
        name: 'commercial-control-stores',
        props: { purpose: 'commercial_control' },
        component: () => import('@/views/Stores/List.vue'),
      }, {
        path: '/public_simulator/stores',
        name: 'public-simulator-stores',
        props: { purpose: 'public_simulator' },
        component: () => import('@/views/Stores/List.vue'),
      }, {
        // Menu, not listing: fixed rows, no collection and no request. The store is not in the
        // path - the host is the one with the id, and the URLs come from it.
        path: '/bank_commission_rules/menu',
        name: 'bank-commission-rules-menu',
        props: { menu: 'bank_commission_rules' },
        component: () => import('@/views/Settings/Menu.vue'),
      }, {
        // Same arrangement as the menu above; each row's destination is a screen of this app,
        // reached by going around through the host, which is the one with the store id.
        path: '/public_simulator/menu',
        name: 'public-simulator-menu',
        props: { menu: 'public_simulator' },
        component: () => import('@/views/Settings/Menu.vue'),
      }, {
        path: '/public_simulator/distribution/form',
        name: 'public-simulator-distribution-form',
        component: () => import('@/views/PublicSimulator/Distribution/Form.vue'),
      }, {
        // The same screen in all three: what differs is the table of items and the group the
        // host writes the URLs under (`CREDERE.settings.<menu>.menu`).
        path: '/settings/menu',
        name: 'settings-menu',
        props: { menu: 'settings' },
        component: () => import('@/views/Settings/Menu.vue'),
      }, {
        path: '/settings/commercial/menu',
        name: 'settings-commercial-menu',
        props: { menu: 'commercial' },
        component: () => import('@/views/Settings/Menu.vue'),
      }, {
        path: '/settings/about/menu',
        name: 'settings-about-menu',
        props: { menu: 'about' },
        component: () => import('@/views/Settings/Menu.vue'),
      }, {
        // The three commercial screens are one: what changes between them is the `purpose`,
        // which chooses the collection and the row's information.
        path: '/financing_return_rules/list',
        name: 'financing-return-rules-list',
        props: { purpose: 'financing_return_rules' },
        component: () => import('@/views/Commercial/List.vue'),
      }, {
        path: '/vehicle_prices/list',
        name: 'vehicle-prices-list',
        props: { purpose: 'vehicle_prices' },
        component: () => import('@/views/Commercial/List.vue'),
      }, {
        path: '/financing_product_rules/list',
        name: 'financing-product-rules-list',
        props: { purpose: 'financing_product_rules' },
        component: () => import('@/views/Commercial/List.vue'),
      }],
    });

    _app.beforeEach(blank);

    _app.beforeEach(async (to) => {
      if (to.meta.public) {
        return true;
      }

      const authStore = useAuthStore();
      const { user } = storeToRefs(authStore);

      if (user.value.id) {
        return true;
      }

      const failure = await authenticate();

      if (failure) {
        return {
          name: 'home-unauthorized',
          query: {
            title: failure.code,
            subtitle: failure.message,
          },
        };
      }

      return true;
    });

    _app.afterEach((to) => {
      const authStore = useAuthStore();
      const { user } = storeToRefs(authStore);

      if (user.value.id) {
        googleAnalytics(to, user.value.id);
      }
    });

    return _app;
  },
};
